import { scaleOrdinal } from 'd3-scale';
import { isUndefined } from 'lodash-es';

import { filterCustomCategories } from './filter-custom-categories.js';
import { getColorsList } from './get-colors-list.js';
import {
  ColorPalette,
  colorPaletteMap,
} from '../../core/types/color-palette.js';
import { DEFAULT_HONEYCOMB_TILE_COLOR } from '../constants.js';
import { CustomCategoricalColorPalette } from '../types/custom-categorical-color-palette.js';

/**
 * Creates an Ordinal Scale based in the name of an existing color palette.
 *
 * @param colorPalette - name of the color palette
 * @param filter - An array of strings with the labels that should be
 *                 included in the output, if nothing is provided, then
 *                 all the categories will be considered
 */
export const buildCategoricalScaleFromColorPalette = (
  colorPalette: ColorPalette,
  filter?: string[],
) => {
  const categories =
    filter ??
    Object.keys(colorPaletteMap[colorPalette]).map((category) => category);
  const colors: string[] = [];
  const colorsList = getColorsList(colorPalette);

  categories.forEach((dataName, i) => {
    const index = i % colorsList.length; // Using module in order to cycle through colorsList
    const colorToken =
      colorPaletteMap[colorPalette][dataName.toLowerCase()] ??
      colorsList[index];
    if (!isUndefined(colorToken)) {
      colors.push(colorToken.Default);
    }
  });

  return scaleOrdinal(categories, colors).unknown(DEFAULT_HONEYCOMB_TILE_COLOR);
};

/**
 * Creates an Ordinal Scale based in a custom color palette.
 *
 * @see {@link CustomCategoricalColorPalette}
 * @param colorScheme - the custom color palette
 * @param filter - An array of strings with the labels that should be
 *                 included in the output, if nothing is provided, then
 *                 all the categories will be considered
 */
export const buildCategoricalScaleFromCustomPalette = (
  colorScheme: CustomCategoricalColorPalette,
  filter?: string[],
) => {
  const categories = filter ?? Object.keys(colorScheme);
  const filteredCategories = filterCustomCategories(colorScheme, categories);
  const labels = Object.keys(filteredCategories);
  const colors = Object.values(filteredCategories);

  return scaleOrdinal(labels, colors).unknown(DEFAULT_HONEYCOMB_TILE_COLOR);
};
